import { createContext, ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';

interface ClinicalModeHeaderConfig {
  title?: string;
  actions?: ReactNode;
  onClose?: () => void;
}

interface ClinicalModeHeaderRegistry {
  register: (id: string, config: ClinicalModeHeaderConfig) => void;
  unregister: (id: string) => void;
}

const NOOP_REGISTRY: ClinicalModeHeaderRegistry = { register: () => undefined, unregister: () => undefined };

// 등록 함수를 값(상태)과 따로 두어, 머리 줄이 다시 그려져도 등록한 쪽이 다시 그려지지 않게 한다.
export const ClinicalModeHeaderRegistryContext = createContext<ClinicalModeHeaderRegistry>(NOOP_REGISTRY);

const mergeHeaderConfigs = (configs: Iterable<ClinicalModeHeaderConfig>): ClinicalModeHeaderConfig => {
  const merged: ClinicalModeHeaderConfig = {};
  for (const config of configs) {
    if (config.title !== undefined) merged.title = config.title;
    if (config.actions !== undefined) merged.actions = config.actions;
    if (config.onClose !== undefined) merged.onClose = config.onClose;
  }
  return merged;
};

export const useClinicalModeHeader = ({ title, actions, onClose }: ClinicalModeHeaderConfig) => {
  const id = useId();
  const { register, unregister } = useContext(ClinicalModeHeaderRegistryContext);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  // 호출한 쪽이 매번 새 함수를 넘겨도 등록이 다시 일어나지 않게 고정된 함수를 올린다.
  const stableOnClose = useCallback(() => onCloseRef.current?.(), []);
  const hasOnClose = onClose !== undefined;

  useEffect(() => {
    register(id, { title, actions, onClose: hasOnClose ? stableOnClose : undefined });
  }, [id, register, title, actions, hasOnClose, stableOnClose]);

  useEffect(() => () => unregister(id), [id, unregister]);
};

export const useClinicalModeHeaderRegistry = () => {
  const [entries, setEntries] = useState<ReadonlyMap<string, ClinicalModeHeaderConfig>>(new Map());
  const register = useCallback((id: string, config: ClinicalModeHeaderConfig) => {
    setEntries((prev) => new Map(prev).set(id, config));
  }, []);
  const unregister = useCallback((id: string) => {
    setEntries((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);
  const registry = useMemo(() => ({ register, unregister }), [register, unregister]);
  const header = mergeHeaderConfigs(entries.values());

  return { registry, header };
};
